import type { TFunction } from 'i18next';
import { Sparkles } from 'lucide-react';
import { useRef, useState, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import i18n from '@/i18n';
import { apiClient } from '@/lib/api-client';
import { usePendingWrite } from '@/lib/use-pending-write';
import { DocumentSlot, type DocumentSlotHandle, type RectifiedImage } from './DocumentSlot';
import { buildSchedaPdf, type SchedaImage } from './pdf';

interface Download {
  readonly label: string;
  readonly href: string;
  readonly filename: string;
}

type ViewState =
  | { status: 'idle' }
  | { status: 'working'; message: string }
  | { status: 'error'; message: string }
  | { status: 'done'; downloads: readonly Download[] };

function sanitizeFilename(raw: string): string {
  const cleaned = raw
    .replace(/[^A-Za-z0-9À-ſ ]/g, '')
    .trim()
    .replace(/\s+/g, '_');
  return cleaned.length > 0 ? cleaned : 'documento';
}

/** Extracts the RFC 9457 problem+json `detail` string from a failed response body, if present. */
function problemDetail(error: unknown): string {
  const raw =
    typeof error === 'object' && error !== null && 'detail' in error
      ? (error as { detail?: unknown }).detail
      : undefined;
  return typeof raw === 'string' ? raw.trim() : '';
}

/**
 * A clean, user-facing message for a failed transcription, keyed on HTTP status. The backend's
 * technical `detail` (English, developer-oriented — e.g. "service not configured") is NOT shown to
 * the user; it is logged to the console for debugging instead (see the caller).
 */
function transcriptionUserMessage(t: TFunction<'errors'>, status: number): string {
  if (status === 401 || status === 403) {
    return t('transcribe.forbidden');
  }
  if (status === 400) {
    return t('transcribe.badImages');
  }
  if (status === 502 || status === 503) {
    return t('transcribe.unavailable');
  }
  return t('transcribe.failed');
}

type Card = Parameters<typeof buildSchedaPdf>[0];

/** Sends the rectified sides to the transcription and returns the card, or throws a user message. */
async function transcribe(
  front: RectifiedImage,
  back: RectifiedImage | null,
  tErrors: TFunction<'errors'>,
): Promise<Card> {
  const { data, error, response } = await apiClient.POST('/v1/identity-documents/transcribe', {
    body: {
      front: { mediaType: front.mediaType, base64Data: front.base64Data },
      ...(back ? { back: { mediaType: back.mediaType, base64Data: back.base64Data } } : {}),
    },
  });
  if (error !== undefined || !response.ok || data === undefined) {
    const detail = problemDetail(error);
    if (detail) {
      console.warn(
        `[identity-documents] transcription failed (HTTP ${response.status}): ${detail}`,
      );
    }
    throw new Error(transcriptionUserMessage(tErrors, response.status));
  }
  return data.card;
}

function schedaImages(front: RectifiedImage, back: RectifiedImage | null): SchedaImage[] {
  // Resolution 8: the PDF is a fixed-Italian regulatory artifact regardless of the active UI
  // locale, so its image captions are resolved through the FIXED 'it' translator, never this
  // component's own active-locale `t` — otherwise an English-UI operator would bake an English
  // "Front"/"Back" caption into the Italian PDF.
  const pdfT = i18n.getFixedT('it', 'identity-documents');
  const images: SchedaImage[] = [{ caption: pdfT('pdf.caption.fronte'), dataUrl: front.dataUrl }];
  if (back) images.push({ caption: pdfT('pdf.caption.retro'), dataUrl: back.dataUrl });
  return images;
}

function buildDownloads(
  t: TFunction<'identity-documents'>,
  card: Card,
  pdfUrl: string,
  front: RectifiedImage,
  back: RectifiedImage | null,
): Download[] {
  const stem = sanitizeFilename(card.intestazioneNome);
  const downloads: Download[] = [
    { label: t('page.pdfDownloadLabel'), href: pdfUrl, filename: `Scheda_${stem}.pdf` },
    { label: t('page.frontDownloadLabel'), href: front.dataUrl, filename: `${stem}_fronte.jpg` },
  ];
  if (back) {
    downloads.push({
      label: t('page.backDownloadLabel'),
      href: back.dataUrl,
      filename: `${stem}_retro.jpg`,
    });
  }
  return downloads;
}

function DownloadsCard({ downloads }: { readonly downloads: readonly Download[] }) {
  const { t } = useTranslation('identity-documents');
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('page.downloadsCardTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <ul className="space-y-2">
          {downloads.map((download) => (
            <li key={download.filename}>
              <a href={download.href} download={download.filename} className="text-brand underline">
                {download.label} — {download.filename}
              </a>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function GenerateBar({
  state,
  busy,
  onGenerate,
}: {
  readonly state: ViewState;
  readonly busy: boolean;
  readonly onGenerate: () => void;
}) {
  const { t } = useTranslation('identity-documents');
  return (
    <div className="flex items-center gap-4">
      {/* The synchronous ref half of SL-0046's guard: the existing `working` state machine
        provides the visible label swap but cannot refuse a second activation inside the
        window before React commits it. */}
      <Button type="button" onClick={onGenerate} disabled={state.status === 'working' || busy}>
        {state.status === 'working' ? t('page.generatingLabel') : t('page.generateButton')}
      </Button>
      {state.status === 'working' && <span className="text-sm">{state.message}</span>}
      {state.status === 'error' && (
        <span className="text-sm text-destructive">{state.message}</span>
      )}
    </div>
  );
}

function ImagesCard({
  frontRef,
  backRef,
}: {
  readonly frontRef: Ref<DocumentSlotHandle>;
  readonly backRef: Ref<DocumentSlotHandle>;
}) {
  const { t } = useTranslation('identity-documents');
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('page.imagesCardTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-6 sm:grid-cols-2">
        <DocumentSlot ref={frontRef} badge={t('page.frontBadge')} hint={t('page.frontHint')} />
        <DocumentSlot ref={backRef} badge={t('page.backBadge')} hint={t('page.backHint')} />
      </CardContent>
    </Card>
  );
}

/**
 * Identity-document module (MVP, feat/identity-documents-mvp). Available to any signed-in
 * @dgbiotech.eu account. Flow: load the front (and optional back) of a document, drag the four
 * corners to deskew each image client-side, then "Genera scheda" rectifies both, sends them to
 * Claude Sonnet via core-api (server-side, EU) for transcription, and produces three EPHEMERAL
 * downloads — the two rectified images and the generated PDF card. Nothing is persisted.
 */
export function IdentityDocumentPage() {
  const { t } = useTranslation('identity-documents');
  const { t: tErrors } = useTranslation('errors');
  const frontRef = useRef<DocumentSlotHandle>(null);
  const backRef = useRef<DocumentSlotHandle>(null);
  const [state, setState] = useState<ViewState>({ status: 'idle' });
  const objectUrlsRef = useRef<string[]>([]);
  const { pending, run } = usePendingWrite();

  const revokePreviousUrls = (): void => {
    for (const url of objectUrlsRef.current) URL.revokeObjectURL(url);
    objectUrlsRef.current = [];
  };

  const generate = async (): Promise<void> => {
    if (!frontRef.current?.hasImage()) {
      setState({ status: 'error', message: t('page.missingFront') });
      return;
    }

    revokePreviousUrls();
    setState({ status: 'working', message: t('page.workingMessage') });

    // Both sides are exported in parallel: each slot has its own image engine.
    let front: RectifiedImage | null;
    let back: RectifiedImage | null;
    try {
      [front, back] = await Promise.all([
        frontRef.current.getRectifiedImage(),
        backRef.current?.getRectifiedImage() ?? null,
      ]);
    } catch {
      setState({ status: 'error', message: t('page.unexpectedError') });
      return;
    }
    if (!front) {
      setState({ status: 'error', message: t('page.missingFront') });
      return;
    }

    try {
      const card = await transcribe(front, back, tErrors);
      const pdfUrl = URL.createObjectURL(buildSchedaPdf(card, schedaImages(front, back)));
      objectUrlsRef.current.push(pdfUrl);
      setState({ status: 'done', downloads: buildDownloads(t, card, pdfUrl, front, back) });
      toast.success(t('page.generatedToast'));
    } catch (error) {
      setState({
        status: 'error',
        message: error instanceof Error ? error.message : t('page.unexpectedError'),
      });
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div className="space-y-1">
        <h2 className="screen-title">{t('page.title')}</h2>
        <p className="screen-summary">{t('page.summary')}</p>
      </div>

      <ImagesCard frontRef={frontRef} backRef={backRef} />

      <GenerateBar state={state} busy={pending} onGenerate={() => void run(() => generate())} />
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Sparkles aria-hidden="true" className="size-4 shrink-0" />
        {t('page.geminiNote')}
      </p>

      {state.status === 'done' && <DownloadsCard downloads={state.downloads} />}
    </div>
  );
}
